import { useState, useCallback } from 'react'
import type { CalendarView } from '../types'
import { addDays, addMonths, startOfWeek } from '../utils/time'

interface UseCalendarNavigationOptions {
  defaultDate?: Date
  defaultView?: CalendarView
  onDateChange?: (date: Date) => void
  onViewChange?: (view: CalendarView) => void
}

export function useCalendarNavigation(options: UseCalendarNavigationOptions = {}) {
  const {
    defaultDate = new Date(),
    defaultView = 'week',
    onDateChange,
    onViewChange,
  } = options

  const [currentDate, setCurrentDate] = useState(defaultDate)
  const [view, setView] = useState<CalendarView>(defaultView)

  const goToDate = useCallback((date: Date) => {
    setCurrentDate(date)
    onDateChange?.(date)
  }, [onDateChange])

  const goToToday = useCallback(() => {
    goToDate(new Date())
  }, [goToDate])

  const goToPrevious = useCallback(() => {
    let newDate: Date

    switch (view) {
      case 'day':
        newDate = addDays(currentDate, -1)
        break
      case 'week':
        newDate = addDays(currentDate, -7)
        break
      case 'month':
        newDate = addMonths(currentDate, -1)
        break
      default:
        newDate = currentDate
    }

    goToDate(newDate)
  }, [currentDate, view, goToDate])

  const goToNext = useCallback(() => {
    let newDate: Date

    switch (view) {
      case 'day':
        newDate = addDays(currentDate, 1)
        break
      case 'week':
        newDate = addDays(currentDate, 7)
        break
      case 'month':
        newDate = addMonths(currentDate, 1)
        break
      default:
        newDate = currentDate
    }

    goToDate(newDate)
  }, [currentDate, view, goToDate])

  const changeView = useCallback((newView: CalendarView) => {
    setView(newView)
    onViewChange?.(newView)
  }, [onViewChange])

  // Calculate derived values
  const weekStart = startOfWeek(currentDate)

  return {
    currentDate,
    view,
    weekStart,
    goToDate,
    goToToday,
    goToPrevious,
    goToNext,
    changeView,
  }
}
